/**
 * tracking.js — cookie consent, analytics and advertising measurement.
 *
 * THE RULES THIS FILE KEEPS (PECR reg. 6 and UK GDPR art. 7)
 *
 *   1. Nothing that is not strictly necessary runs until the person chooses.
 *      No script tag, no cookie, no ping. Google's "consent mode" would still
 *      send cookieless pings before a choice; we do not load Google at all
 *      until analytics is switched on.
 *   2. Saying no is as easy as saying yes: the banner offers Accept all and
 *      Reject all side by side, with the same weight.
 *   3. Each purpose is a separate choice, never pre-ticked.
 *   4. Changing your mind is as easy as deciding: "Cookie settings" at the foot
 *      of every public page and in Settings → Privacy reopens the same panel.
 *      Withdrawing reloads the page, so nothing already loaded keeps running.
 *   5. The choice is recorded (what, when, which policy version) on the device
 *      and, for a signed-in account, in users/{uid}/private/settings.
 *   6. A choice lasts 12 months, then the banner asks again.
 *
 * WHAT IS SENT, AND WHAT NEVER IS
 *
 * Analytics (Google Analytics 4) receives page addresses with every identifier
 * replaced by a placeholder — a profile is "/u/:handle", a post "/post/:id" —
 * and the page title replaced by that same template, because titles carry
 * people's names. Search terms and query strings are dropped. Events carry
 * coarse, non-identifying properties only (a study type, a format). No user
 * id, email, name, post, message, question or search is ever sent. Google
 * signals and ad personalisation are off unless marketing is also allowed.
 *
 * Marketing tags (LinkedIn Insight, Meta Pixel, Google Ads) run only on the
 * public pages — home, sign-in, legal and help — and fire one conversion when
 * an account is created. They are never loaded inside the signed-in app,
 * where the content is clinical.
 *
 * Every vendor is configured by an environment variable. A vendor without an
 * id is not loaded and not offered in the banner, so the banner never asks
 * permission for something that does not exist.
 */

const env = import.meta.env ?? {};

export const GA_ID = String(env.VITE_GA_MEASUREMENT_ID || '').trim();
const LINKEDIN_ID = String(env.VITE_LINKEDIN_PARTNER_ID || '').trim();
const LINKEDIN_SIGNUP = String(env.VITE_LINKEDIN_SIGNUP_CONVERSION_ID || '').trim();
const META_ID = String(env.VITE_META_PIXEL_ID || '').trim();
const ADS_ID = String(env.VITE_GOOGLE_ADS_ID || '').trim();
const ADS_SIGNUP_LABEL = String(env.VITE_GOOGLE_ADS_SIGNUP_LABEL || '').trim();

/** Ids must look like ids before they go anywhere near a script URL. */
const valid = {
  ga: /^G-[A-Z0-9]{4,20}$/.test(GA_ID),
  linkedin: /^\d{3,12}$/.test(LINKEDIN_ID),
  meta: /^\d{6,20}$/.test(META_ID),
  ads: /^AW-\d{6,15}$/.test(ADS_ID),
};

/** Analytics is offered whenever a measurement id is configured. */
export const ANALYTICS_AVAILABLE = valid.ga;
/** Marketing is offered only when at least one advertising tag is configured. */
export const MARKETING_AVAILABLE = valid.linkedin || valid.meta || valid.ads;
/** Whether there is anything optional to ask about at all. */
export const CONSENT_NEEDED = ANALYTICS_AVAILABLE || MARKETING_AVAILABLE;

/** Bump when the purposes or vendors change in a way people must be re-asked about. */
export const CONSENT_VERSION = '2026-10-04';
const KEY = 'leeflet.consent';
const MAX_AGE_MS = 365 * 24 * 3600 * 1000;
const EVENT = 'leeflet:consent';
const OPEN_EVENT = 'leeflet:open-consent';

/** Global Privacy Control: honoured as "no advertising" whatever is clicked. */
export const GPC = typeof navigator !== 'undefined' && navigator.globalPrivacyControl === true;

/* ── The record ───────────────────────────────────────────────────────────── */

function readStored() {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const c = JSON.parse(raw);
    if (!c || typeof c !== 'object') return null;
    return {
      v: String(c.v || ''),
      at: String(c.at || ''),
      analytics: c.analytics === true,
      marketing: c.marketing === true,
    };
  } catch {
    return null;
  }
}

/** The current choice, or null when none has been made (or it has lapsed). */
export function getConsent() {
  const c = readStored();
  if (!c) return null;
  if (c.v !== CONSENT_VERSION) return null;
  const at = Date.parse(c.at);
  if (!Number.isFinite(at) || Date.now() - at > MAX_AGE_MS) return null;
  return c;
}

/** True when the banner should be shown. */
export function needsConsentPrompt() {
  return CONSENT_NEEDED && getConsent() === null;
}

/**
 * Record a choice. Unavailable purposes are stored as false, and GPC forces
 * marketing off. Withdrawing something that was on reloads the page, because
 * a script already running cannot be unloaded any other way.
 */
export function setConsent({ analytics = false, marketing = false }) {
  const before = getConsent();
  const next = {
    v: CONSENT_VERSION,
    at: new Date().toISOString(),
    analytics: ANALYTICS_AVAILABLE && analytics === true,
    marketing: MARKETING_AVAILABLE && marketing === true && !GPC,
  };
  try { window.localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* private mode */ }

  const withdrew = (before?.analytics && !next.analytics) || (before?.marketing && !next.marketing);
  if (withdrew) {
    clearTrackingCookies();
    window.location.reload();
    return next;
  }
  window.dispatchEvent(new CustomEvent(EVENT, { detail: next }));
  return next;
}

export function onConsentChange(fn) {
  const handler = (e) => fn(e.detail);
  window.addEventListener(EVENT, handler);
  return () => window.removeEventListener(EVENT, handler);
}

/** Reopen the choices panel from anywhere ("Cookie settings" links). */
export function openConsentSettings() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

export function onOpenConsentSettings(fn) {
  window.addEventListener(OPEN_EVENT, fn);
  return () => window.removeEventListener(OPEN_EVENT, fn);
}

/** First-party cookies the vendors set on our own domain. */
function clearTrackingCookies() {
  const names = document.cookie.split(';').map((c) => c.split('=')[0].trim())
    .filter((n) => /^(_ga|_gid|_gat|_gcl|_fbp|_fbc|li_|lidc|bcookie|AnalyticsSyncHistory|UserMatchHistory)/.test(n));
  const host = window.location.hostname;
  const domains = ['', host, `.${host}`, `.${host.split('.').slice(-2).join('.')}`];
  for (const name of names) {
    for (const d of domains) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d ? `; domain=${d}` : ''}`;
    }
  }
}

/* ── Paths ────────────────────────────────────────────────────────────────── */

const TEMPLATES = [
  [/^\/post\/[^/]+$/, '/post/:id'],
  [/^\/u\/id\/[^/]+$/, '/u/id/:uid'],
  [/^\/u\/[^/]+$/, '/u/:handle'],
  [/^\/messages\/[^/]+$/, '/messages/:conversation'],
  [/^\/groups\/[^/]+$/, '/groups/:group'],
  [/^\/tag\/[^/]+$/, '/tag/:tag'],
  [/^\/write\/[^/]+$/, '/write/:id'],
];

/** A path with every identifier replaced by a placeholder, and no query. */
export function pathTemplate(pathname) {
  const p = String(pathname || '/').split('?')[0].split('#')[0] || '/';
  for (const [re, t] of TEMPLATES) if (re.test(p)) return t;
  return p;
}

/** The pages marketing tags may run on. Never the signed-in app. */
export function isPublicMarketingPath(pathname) {
  const p = pathTemplate(pathname);
  return p === '/' || p === '/signin' || p === '/support' || p === '/legal' || p.startsWith('/legal/');
}

/* ── Loading ──────────────────────────────────────────────────────────────── */

function loadScript(src, attrs = {}) {
  if (document.querySelector(`script[src="${src}"]`)) return;
  const s = document.createElement('script');
  s.async = true;
  s.src = src;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  document.head.appendChild(s);
}

let gaReady = false;
function gtag() {
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

function ensureGtag(consent) {
  if (!window.dataLayer) window.dataLayer = [];
  if (!window.gtag) window.gtag = gtag;
  if (gaReady) return;
  window.gtag('consent', 'default', {
    analytics_storage: consent.analytics ? 'granted' : 'denied',
    ad_storage: consent.marketing ? 'granted' : 'denied',
    ad_user_data: consent.marketing ? 'granted' : 'denied',
    ad_personalization: consent.marketing ? 'granted' : 'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    security_storage: 'granted',
  });
  window.gtag('js', new Date());
  const id = valid.ga ? GA_ID : ADS_ID;
  loadScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`);
  gaReady = true;
}

let analyticsOn = false;
function startAnalytics(consent) {
  if (analyticsOn || !consent.analytics || !valid.ga) return;
  ensureGtag(consent);
  window.gtag('config', GA_ID, {
    send_page_view: false,                       // sent by hand, with a template path
    allow_google_signals: consent.marketing,
    allow_ad_personalization_signals: consent.marketing,
    cookie_flags: 'SameSite=Lax;Secure',
    // 13 months, as the Cookie Policy says — Google's default is two years.
    cookie_expires: 395 * 24 * 3600,
  });
  analyticsOn = true;
}

let marketingOn = false;
function startMarketing(consent, pathname) {
  if (marketingOn || !consent.marketing || !isPublicMarketingPath(pathname)) return;
  marketingOn = true;

  if (valid.ads) {
    ensureGtag(consent);
    window.gtag('config', ADS_ID, { send_page_view: false });
  }
  if (valid.linkedin) {
    window._linkedin_partner_id = LINKEDIN_ID;
    window._linkedin_data_partner_ids = [LINKEDIN_ID];
    window.lintrk = window.lintrk || function lintrk(a, b) { (window.lintrk.q = window.lintrk.q || []).push([a, b]); };
    loadScript('https://snap.licdn.com/li.lms-analytics/insight.min.js');
  }
  if (valid.meta) {
    /* eslint-disable */
    if (!window.fbq) {
      const n = window.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
      if (!window._fbq) window._fbq = n;
      n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
    }
    /* eslint-enable */
    // No automatic events, and no page views from history changes: the pixel
    // must not follow anyone from the home page into the app.
    window.fbq.disablePushState = true;
    window.fbq('set', 'autoConfig', false, META_ID);
    window.fbq('init', META_ID);
    window.fbq('track', 'PageView');
    loadScript('https://connect.facebook.net/en_US/fbevents.js');
  }
}

/** Apply the current choice for the page being shown. Safe to call often. */
export function applyConsent(pathname) {
  const c = getConsent();
  if (!c) return;
  startAnalytics(c);
  startMarketing(c, pathname);
}

/* ── Measuring ────────────────────────────────────────────────────────────── */

/** One page view, with the identifier-free template as both path and title. */
export function trackPage(pathname) {
  if (!analyticsOn || !window.gtag) return;
  const path = pathTemplate(pathname);
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: `${window.location.origin}${path}`,
    page_title: path,
  });
}

/**
 * The events Clincordant measures, and the only properties each may carry. Anything
 * else passed in is dropped, so a careless call cannot leak a name or a search.
 */
const EVENTS = {
  sign_up: ['method'],
  login: ['method'],
  onboarding_complete: [],
  briefing_view: ['cards', 'cached'],
  briefing_refresh: [],
  paper_open: ['study_type', 'evidence_grade'],
  paper_save: ['study_type'],
  ask_question: ['mode'],
  post_create: ['format', 'in_group'],
  group_join: [],
  plus_request: [],
};

export function track(name, props = {}) {
  if (!analyticsOn || !window.gtag || !EVENTS[name]) return;
  const clean = {};
  for (const k of EVENTS[name]) {
    const v = props[k];
    if (typeof v === 'number' && Number.isFinite(v)) clean[k] = v;
    else if (typeof v === 'boolean') clean[k] = v;
    else if (typeof v === 'string' && v) clean[k] = v.slice(0, 40);
  }
  window.gtag('event', name, clean);
}

/** The one advertising conversion: an account was created. */
export function trackSignupConversion(method) {
  track('sign_up', { method });
  const c = getConsent();
  if (!c?.marketing || !marketingOn) return;
  if (valid.ads && ADS_SIGNUP_LABEL && window.gtag) {
    window.gtag('event', 'conversion', { send_to: `${ADS_ID}/${ADS_SIGNUP_LABEL}` });
  }
  if (valid.linkedin && /^\d+$/.test(LINKEDIN_SIGNUP) && window.lintrk) {
    window.lintrk('track', { conversion_id: Number(LINKEDIN_SIGNUP) });
  }
  if (valid.meta && window.fbq) window.fbq('track', 'CompleteRegistration');
}
